import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowRight, RotateCcw, User } from "lucide-react";

import { CalculatorLayout } from "@/components/calculator/CalculatorLayout";
import { NumberInput } from "@/components/calculator/NumberInput";
import { CopyResultButton } from "@/components/calculator/CopyResultButton";
import { ShareButton } from "@/components/calculator/ShareButton";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/lib/store";
import { toBengaliNumerals } from "@/lib/bdNumerals";
import { buildSeo } from "@/lib/seo";
import { cn } from "@/lib/utils";

import {
  computeBodyType,
  somatoXY,
  validate,
  type BodyTypeResult,
  type Gender,
  type SomatotypeKey,
} from "@/lib/calculators/bodyType";

type Bilingual = { en: string; bn: string };

const STR = {
  h1: { en: "Body Type Calculator", bn: "বডি টাইপ ক্যালকুলেটর" },
  desc: {
    en: "Find your somatotype — ectomorph, mesomorph or endomorph — using a simplified Heath-Carter assessment based on body measurements.",
    bn: "Heath-Carter পদ্ধতিতে আপনার বডি টাইপ (ইক্টোমর্ফ, মেসোমর্ফ, এন্ডোমর্ফ) জানুন — শরীরের মাপের উপর ভিত্তি করে।",
  },
  gender: { en: "Gender", bn: "লিঙ্গ" },
  male: { en: "Male", bn: "পুরুষ" },
  female: { en: "Female", bn: "মহিলা" },
  age: { en: "Age", bn: "বয়স" },
  ageUnit: { en: "years", bn: "বছর" },
  height: { en: "Height (cm)", bn: "উচ্চতা (সেমি)" },
  weight: { en: "Weight (kg)", bn: "ওজন (কেজি)" },
  wrist: { en: "Wrist (cm)", bn: "কব্জি (সেমি)" },
  ankle: { en: "Ankle (cm)", bn: "গোড়ালি (সেমি)" },
  hip: { en: "Hip (cm)", bn: "নিতম্ব (সেমি)" },
  waist: { en: "Waist (cm)", bn: "কোমর (সেমি)" },
  reset: { en: "Reset", bn: "রিসেট" },

  yourType: { en: "Body Type", bn: "বডি টাইপ" },
  scores: { en: "Component scores (1–7)", bn: "কম্পোনেন্ট স্কোর (১–৭)" },
  endomorphy: { en: "Endomorphy (fatness)", bn: "এন্ডোমর্ফি (চর্বি)" },
  mesomorphy: { en: "Mesomorphy (muscularity)", bn: "মেসোমর্ফি (পেশী)" },
  ectomorphy: { en: "Ectomorphy (slenderness)", bn: "ইক্টোমর্ফি (চিকন)" },
  frame: { en: "Frame", bn: "ফ্রেম" },
  small: { en: "Small", bn: "ছোট" },
  medium: { en: "Medium", bn: "মাঝারি" },
  large: { en: "Large", bn: "বড়" },

  shapeXref: {
    en: "Looking for body shape (pear / apple / hourglass) for clothing? Try the Body Shape Calculator.",
    bn: "পোশাকের জন্য বডি শেপ (পেয়ার / আপেল / আওয়ারগ্লাস) দরকার? Body Shape Calculator দেখুন।",
  },
  goShape: { en: "Body Shape Calculator", bn: "Body Shape Calculator" },

  disclaimer: {
    en: "Body type is one factor among many. Genetics, lifestyle, training and nutrition matter much more than any 'somatotype label'. Use this as general information, not a strict program.",
    bn: "বডি টাইপ অনেক কারণের একটি মাত্র। জেনেটিক্স, জীবনযাপন, ব্যায়াম ও পুষ্টি 'সোমাটোটাইপ' এর চেয়ে অনেক বেশি গুরুত্বপূর্ণ। এটি সাধারণ তথ্য হিসেবে ব্যবহার করুন, কঠোর নিয়ম নয়।",
  },
};

const TYPE_NAME: Record<SomatotypeKey, Bilingual> = {
  endomorph: { en: "Endomorph", bn: "এন্ডোমর্ফ" },
  mesomorph: { en: "Mesomorph", bn: "মেসোমর্ফ" },
  ectomorph: { en: "Ectomorph", bn: "ইক্টোমর্ফ" },
  endo_mesomorph: { en: "Endo-Mesomorph", bn: "এন্ডো-মেসোমর্ফ" },
  meso_endomorph: { en: "Meso-Endomorph", bn: "মেসো-এন্ডোমর্ফ" },
  ecto_mesomorph: { en: "Ecto-Mesomorph", bn: "ইক্টো-মেসোমর্ফ" },
  meso_ectomorph: { en: "Meso-Ectomorph", bn: "মেসো-ইক্টোমর্ফ" },
  ecto_endomorph: { en: "Ecto-Endomorph", bn: "ইক্টো-এন্ডোমর্ফ" },
  endo_ectomorph: { en: "Endo-Ectomorph", bn: "এন্ডো-ইক্টোমর্ফ" },
  central: { en: "Central (balanced)", bn: "ব্যালান্সড / সেন্ট্রাল" },
};

const TYPE_TRAITS: Record<"endo" | "meso" | "ecto", { name: Bilingual; traits: Bilingual[]; fitness: Bilingual[] }> = {
  endo: {
    name: { en: "Endomorph", bn: "এন্ডোমর্ফ" },
    traits: [
      { en: "Softer, rounder build with larger frame", bn: "নরম, গোলাকার গঠন ও বড় ফ্রেম" },
      { en: "Gains weight (fat and muscle) easily", bn: "সহজে ওজন (চর্বি ও পেশী) বাড়ে" },
      { en: "Slower resting metabolism", bn: "বিশ্রামে বিপাক ধীর" },
    ],
    fitness: [
      { en: "Mix of strength and regular cardio works well", bn: "শক্তি প্রশিক্ষণ ও নিয়মিত কার্ডিও ভালো কাজ করে" },
      { en: "Mind portion sizes; protein at every meal", bn: "প্রতিবেলার খাদ্য পরিমাণ ও প্রোটিন গুরুত্বপূর্ণ" },
      { en: "Sleep and stress management strongly affect outcomes", bn: "ঘুম ও মানসিক চাপ ব্যবস্থাপনা ফলাফলে বড় প্রভাব ফেলে" },
    ],
  },
  meso: {
    name: { en: "Mesomorph", bn: "মেসোমর্ফ" },
    traits: [
      { en: "Athletic, muscular build with defined shoulders", bn: "অ্যাথলেটিক, পেশীবহুল ও সুনির্দিষ্ট কাঁধ" },
      { en: "Gains muscle and loses fat relatively easily", bn: "তুলনামূলকভাবে সহজে পেশী বাড়ে ও চর্বি কমে" },
      { en: "Medium-large frame, naturally strong", bn: "মাঝারি-বড় ফ্রেম, প্রাকৃতিকভাবে শক্তিশালী" },
    ],
    fitness: [
      { en: "Responds well to strength training", bn: "শক্তি প্রশিক্ষণে দ্রুত সাড়া দেয়" },
      { en: "Tolerant of varied diet — balanced macros work fine", bn: "বিভিন্ন খাদ্য সহ্য করে — সুষম ম্যাক্রো যথেষ্ট" },
      { en: "Cardio for cardiovascular health, not just fat loss", bn: "চর্বি কমানোর বাইরে হৃদরোগ প্রতিরোধেও কার্ডিও জরুরি" },
    ],
  },
  ecto: {
    name: { en: "Ectomorph", bn: "ইক্টোমর্ফ" },
    traits: [
      { en: "Naturally slim, narrow shoulders, small frame", bn: "প্রাকৃতিকভাবে চিকন, সরু কাঁধ, ছোট ফ্রেম" },
      { en: "Hard to gain weight or muscle", bn: "ওজন বা পেশী বাড়ানো কঠিন" },
      { en: "Faster resting metabolism", bn: "বিশ্রামে বিপাক দ্রুত" },
    ],
    fitness: [
      { en: "Focus on compound strength lifts; minimise excessive cardio", bn: "যৌগিক ভার উত্তোলনে মনোযোগ; অতিরিক্ত কার্ডিও কমান" },
      { en: "Eat in calorie surplus with adequate protein (1.6–2 g/kg)", bn: "ক্যালরি উদ্বৃত্তে খান, প্রোটিন ১.৬–২ গ্রাম/কেজি" },
      { en: "Prioritise rest — recovery is often the bottleneck", bn: "বিশ্রামকে গুরুত্ব দিন — পুনরুদ্ধার প্রায়ই বাধা" },
    ],
  },
};

const FAQ: { q: Bilingual; a: Bilingual }[] = [
  {
    q: { en: "What is a body type / somatotype?", bn: "বডি টাইপ বা সোমাটোটাইপ কী?" },
    a: {
      en: "A somatotype is a 3-component description of body composition introduced by W.H. Sheldon (1940) and refined by Heath & Carter (1967). It rates a person on endomorphy (fatness), mesomorphy (muscularity) and ectomorphy (slenderness), each from 1 to 7.",
      bn: "সোমাটোটাইপ হলো শরীরের গঠনের ৩-উপাদান বর্ণনা — Sheldon (১৯৪০) প্রবর্তিত, Heath ও Carter (১৯৬৭) দ্বারা উন্নত। এটি একজন ব্যক্তিকে এন্ডোমর্ফি (চর্বি), মেসোমর্ফি (পেশী) ও ইক্টোমর্ফি (চিকন) — প্রতিটি ১ থেকে ৭ এ মূল্যায়ন করে।",
    },
  },
  {
    q: { en: "Can I change my body type?", bn: "বডি টাইপ কি বদলানো যায়?" },
    a: {
      en: "You cannot change your underlying skeletal frame, but you can significantly change your body composition through training and nutrition. Many 'endomorphs' lean down and many 'ectomorphs' build muscle — the label is informational, not destiny.",
      bn: "মূল কঙ্কাল বদলানো যায় না, কিন্তু ব্যায়াম ও পুষ্টির মাধ্যমে দেহগঠন অনেক বদলানো যায়। অনেক 'এন্ডোমর্ফ' চিকন হন ও অনেক 'ইক্টোমর্ফ' পেশী বানান — লেবেল শুধু তথ্য, ভাগ্য নয়।",
    },
  },
  {
    q: { en: "Best workout for ectomorph / mesomorph / endomorph?", bn: "প্রতিটি বডি টাইপের জন্য সেরা ব্যায়াম?" },
    a: {
      en: "Ectomorphs benefit from compound lifts and a calorie surplus; mesomorphs from balanced strength + cardio; endomorphs from consistent strength training, cardio, and portion control. All three should prioritise sleep, protein, and progression.",
      bn: "ইক্টোমর্ফ — যৌগিক ভার ও ক্যালরি উদ্বৃত্ত; মেসোমর্ফ — সুষম শক্তি + কার্ডিও; এন্ডোমর্ফ — নিয়মিত শক্তি প্রশিক্ষণ, কার্ডিও ও পরিমাণ নিয়ন্ত্রণ। সবার জন্য ঘুম, প্রোটিন ও ধারাবাহিক উন্নতি জরুরি।",
    },
  },
  {
    q: { en: "Body type vs body shape — what is the difference?", bn: "বডি টাইপ ও বডি শেপের পার্থক্য কী?" },
    a: {
      en: "Body type (somatotype) describes overall composition (fatness, muscularity, slenderness). Body shape (pear, apple, hourglass, rectangle, inverted triangle) describes silhouette for clothing fit. They are different concepts.",
      bn: "বডি টাইপ (সোমাটোটাইপ) মোট গঠন (চর্বি, পেশী, চিকন) বর্ণনা করে। বডি শেপ (পেয়ার, আপেল, আওয়ারগ্লাস, রেক্ট্যাঙ্গেল, ইনভার্টেড ট্রায়াঙ্গেল) পোশাকের জন্য সিলুয়েট বোঝায়। দুটি ভিন্ন ধারণা।",
    },
  },
  {
    q: { en: "Is somatotype scientifically valid?", bn: "সোমাটোটাইপ কি বিজ্ঞানসম্মত?" },
    a: {
      en: "It is a research-grade method (Heath-Carter is widely used in sports science) but its 'fitness implications' have been overstated in popular culture. Use the score directionally, not as a strict program.",
      bn: "Heath-Carter পদ্ধতি ক্রীড়া বিজ্ঞানে বহুল ব্যবহৃত — কিন্তু জনপ্রিয় সংস্কৃতিতে এর 'ফিটনেস প্রভাব' অতিরঞ্জিত। স্কোরকে দিকনির্দেশনা হিসেবে দেখুন, কঠোর নিয়ম হিসেবে নয়।",
    },
  },
  {
    q: { en: "বডি টাইপ কী?", bn: "বডি টাইপ কী?" },
    a: {
      en: "বডি টাইপ হলো আপনার শরীরের সাধারণ গঠন — চর্বি (এন্ডোমর্ফ), পেশী (মেসোমর্ফ), নাকি চিকন (ইক্টোমর্ফ) — তার সমন্বয়।",
      bn: "বডি টাইপ হলো আপনার শরীরের সাধারণ গঠন — চর্বি (এন্ডোমর্ফ), পেশী (মেসোমর্ফ), নাকি চিকন (ইক্টোমর্ফ) — তার সমন্বয়।",
    },
  },
];

export const Route = createFileRoute("/body-type-calculator")({
  head: () => {
    const seo = buildSeo({
      title: "Body Type Calculator — Ectomorph, Mesomorph",
      description:
        "Free body type (somatotype) calculator. Determine if you're ectomorph, mesomorph, or endomorph based on body measurements. বডি টাইপ ক্যালকুলেটর।",
      path: "/body-type-calculator",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Body Type Calculator", path: "/body-type-calculator" },
      ],
      faq: FAQ.map((f) => ({ q: f.q.en, a: f.a.en })),
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: BodyTypePage,
});

function BodyTypePage() {
  const lang = useAppStore((s) => s.lang);
  const tr = (k: keyof typeof STR) => (STR[k] as Bilingual)[lang];
  const num = (n: number | string) => (lang === "bn" ? toBengaliNumerals(n) : String(n));
  const fmt = (n: number) => num(n.toFixed(1));

  const [gender, setGender] = useState<Gender>("male");
  const [age, setAge] = useState("30");
  const [height, setHeight] = useState("175");
  const [weight, setWeight] = useState("80");
  const [wrist, setWrist] = useState("18");
  const [ankle, setAnkle] = useState("23");
  const [hip, setHip] = useState("98");
  const [waist, setWaist] = useState("88");

  const inp = {
    gender,
    age: parseFloat(age),
    heightCm: parseFloat(height),
    weightKg: parseFloat(weight),
    wristCm: parseFloat(wrist),
    ankleCm: parseFloat(ankle),
    hipCm: parseFloat(hip),
    waistCm: parseFloat(waist),
  };

  const result: BodyTypeResult | null = useMemo(() => {
    const v = validate(inp);
    if (!v.ok) return null;
    return computeBodyType(inp as Parameters<typeof computeBodyType>[0]);
  }, [gender, age, height, weight, wrist, ankle, hip, waist]);

  const reset = () => {
    setGender("male"); setAge("30");
    setHeight("175"); setWeight("80");
    setWrist("18"); setAnkle("23");
    setHip("98"); setWaist("88");
  };

  const dominantTraits = result ? TYPE_TRAITS[result.dominant] : null;
  const shareLine = result ? `${tr("yourType")}: ${TYPE_NAME[result.type][lang]} (${num(result.endo)}-${num(result.meso)}-${num(result.ecto)})` : "";

  return (
    <CalculatorLayout
      slug="body-type-calculator"
      title={tr("h1")}
      description={tr("desc")}
      iconName="User"
      faq={FAQ.map((f) => ({ q: f.q[lang], a: f.a[lang] }))}
    >
      {/* Cross-link to body shape */}
      <div className={cn("mb-5 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
        {tr("shapeXref")}{" "}
        <Link to="/body-shape-calculator" className="font-medium text-accent hover:underline">
          {tr("goShape")} →
        </Link>
      </div>

      {/* Gender */}
      <div className="space-y-1.5">
        <label className={cn("block text-sm font-medium", lang === "bn" && "font-bn")}>{tr("gender")}</label>
        <div className="inline-flex rounded-lg border border-border bg-muted p-0.5">
          {(["male", "female"] as Gender[]).map((g) => {
            const active = g === gender;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGender(g)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition",
                  active ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
                  lang === "bn" && "font-bn",
                )}
              >
                {g === "male" ? tr("male") : tr("female")}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inputs */}
      <div className="mt-5 grid gap-5 md:grid-cols-2">
        <NumberInput label={tr("age")} value={age} onChange={setAge} unit={tr("ageUnit")} />
        <NumberInput label={tr("height")} value={height} onChange={setHeight} unit="cm" />
        <NumberInput label={tr("weight")} value={weight} onChange={setWeight} unit="kg" />
        <NumberInput label={tr("waist")} value={waist} onChange={setWaist} unit="cm" />
        <NumberInput label={tr("hip")} value={hip} onChange={setHip} unit="cm" />
        <NumberInput label={tr("wrist")} value={wrist} onChange={setWrist} unit="cm" />
        <NumberInput label={tr("ankle")} value={ankle} onChange={setAnkle} unit="cm" />
      </div>

      <div className="mt-5 flex justify-end">
        <Button variant="outline" onClick={reset} className="gap-2">
          <RotateCcw size={14} />
          {tr("reset")}
        </Button>
      </div>

      {result && dominantTraits && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mt-8 space-y-6"
        >
          {/* Primary */}
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={cn("text-xs font-semibold uppercase tracking-wide text-accent", lang === "bn" && "font-bn")}>
                  {tr("yourType")}
                </p>
                <p className="mt-1 flex items-baseline gap-2 text-3xl font-semibold text-foreground md:text-4xl">
                  <User size={26} className="text-accent" />
                  <span className={cn(lang === "bn" && "font-bn")}>{TYPE_NAME[result.type][lang]}</span>
                </p>
                <p className={cn("mt-2 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
                  {tr("scores")}: <span className="font-semibold tabular-nums text-foreground">{num(result.endo)}-{num(result.meso)}-{num(result.ecto)}</span>
                  <span className="ml-2 text-xs">({tr("endomorphy").split(" ")[0]} – {tr("mesomorphy").split(" ")[0]} – {tr("ectomorphy").split(" ")[0]})</span>
                </p>
                <p className={cn("mt-1 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {tr("frame")}: <span className="font-medium text-foreground">{tr(result.frame)}</span>
                  <span className="ml-3">BMI: <span className="tabular-nums">{num(result.bmi)}</span></span>
                  <span className="ml-3">HWR: <span className="tabular-nums">{num(result.hwr)}</span></span>
                </p>
              </div>
              <CopyResultButton text={shareLine} />
            </div>
          </div>

          {/* Component bars + triangle */}
          <div className="grid gap-5 md:grid-cols-[1.2fr_1fr]">
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>{tr("scores")}</h3>
              <ScoreBar label={tr("endomorphy")} value={result.endo} color="bg-orange-500" lang={lang} fmt={fmt} />
              <ScoreBar label={tr("mesomorphy")} value={result.meso} color="bg-emerald-500" lang={lang} fmt={fmt} />
              <ScoreBar label={tr("ectomorphy")} value={result.ecto} color="bg-sky-500" lang={lang} fmt={fmt} />
            </div>
            <SomatoTriangle result={result} lang={lang} />
          </div>

          {/* Type description */}
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className={cn("mb-3 text-sm font-bold", lang === "bn" && "font-bn")}>
              {dominantTraits.name[lang]} — {lang === "bn" ? "বৈশিষ্ট্য" : "characteristics"}
            </h3>
            <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {dominantTraits.traits.map((t, i) => <li key={i}>{t[lang]}</li>)}
            </ul>
            <h4 className={cn("mt-4 mb-2 text-sm font-bold", lang === "bn" && "font-bn")}>
              {lang === "bn" ? "ফিটনেস বিবেচনা (তথ্যমূলক)" : "Fitness considerations (informational)"}
            </h4>
            <ul className={cn("list-disc space-y-1 pl-5 text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
              {dominantTraits.fitness.map((t, i) => <li key={i}>{t[lang]}</li>)}
            </ul>
            <div className="mt-3 flex flex-wrap gap-3 text-xs">
              <Link to="/calorie-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">
                Calorie Calculator <ArrowRight size={12} />
              </Link>
              <Link to="/bmr-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">
                BMR Calculator <ArrowRight size={12} />
              </Link>
              <Link to="/body-fat-calculator" className="inline-flex items-center gap-1 text-accent hover:underline">
                Body Fat <ArrowRight size={12} />
              </Link>
            </div>
          </div>

          {/* Disclaimer */}
          <div className={cn(
            "flex items-start gap-3 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4 text-xs text-foreground/80",
            lang === "bn" && "font-bn",
          )}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-yellow-500" />
            <p>{tr("disclaimer")}</p>
          </div>

          <div className="flex justify-end">
            <ShareButton title={shareLine} />
          </div>
        </motion.div>
      )}

      <article className="mt-12 space-y-10">
        <Section title={lang === "bn" ? "সোমাটোটাইপ কী?" : "What are somatotypes?"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "মনোবিজ্ঞানী W.H. Sheldon ১৯৪০-এর দশকে মানুষের শরীরকে তিনটি কম্পোনেন্টে বিভক্ত করেন: এন্ডোমর্ফ (নরম/গোলাকার), মেসোমর্ফ (পেশীবহুল), এবং ইক্টোমর্ফ (চিকন)। Heath ও Carter (১৯৬৭) এটিকে ক্রীড়া বিজ্ঞানে ব্যবহারযোগ্য পরিমাপ পদ্ধতিতে রূপান্তরিত করেন।"
              : "Psychologist W.H. Sheldon in the 1940s classified bodies into three components: endomorph (soft/round), mesomorph (muscular), and ectomorph (slim). Heath & Carter (1967) turned this into a measurable system used in sports science."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "তিন ধরনের সোমাটোটাইপ" : "The three somatotypes"} lang={lang}>
          <div className="grid gap-3 md:grid-cols-3">
            {(["endo", "meso", "ecto"] as const).map((k) => (
              <div key={k} className="rounded-xl border border-border bg-card p-4">
                <h3 className={cn("mb-2 text-sm font-bold", lang === "bn" && "font-bn")}>{TYPE_TRAITS[k].name[lang]}</h3>
                <ul className={cn("list-disc space-y-1 pl-4 text-xs text-muted-foreground", lang === "bn" && "font-bn")}>
                  {TYPE_TRAITS[k].traits.map((t, i) => <li key={i}>{t[lang]}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Section>

        <Section title={lang === "bn" ? "মিশ্র টাইপ" : "Mixed types"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "অধিকাংশ মানুষ বিশুদ্ধ এক টাইপের নন — দুই টাইপের মিশ্রণ থাকে (যেমন ইক্টো-মেসো, মেসো-এন্ডো)। কিছু মানুষ 'সেন্ট্রাল' হন — তিন উপাদান প্রায় সমান।"
              : "Most people are not a single pure type — they fall between two (e.g. Ecto-Mesomorph, Meso-Endomorph). Some are 'central' with all three components nearly equal."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "সোমাটোটাইপ ভাগ্য নয়" : "Somatotype is not destiny"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "আপনার কঙ্কাল ও বংশগতি বদলানো না গেলেও, প্রশিক্ষণ ও পুষ্টির মাধ্যমে দেহগঠন উল্লেখযোগ্যভাবে বদলানো যায়। 'এন্ডোমর্ফ' হিসেবে চিহ্নিত হলেও আপনি চিকন হতে পারেন; 'ইক্টোমর্ফ' পেশী বানাতে পারেন। লেবেলকে দিকনির্দেশনা হিসেবে নিন, সীমা হিসেবে নয়।"
              : "While you cannot change your skeleton, you can dramatically change body composition through training and nutrition. An 'endomorph' can lean down; an 'ectomorph' can build muscle. Treat the label as direction, not a ceiling."}
          </p>
        </Section>

        <Section title={lang === "bn" ? "বডি টাইপ বনাম বডি শেপ" : "Body type vs body shape"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>
            {lang === "bn"
              ? "এই ক্যালকুলেটর সোমাটোটাইপ (এন্ডো/মেসো/ইক্টো) নির্ণয় করে। পোশাকের জন্য সিলুয়েট-ভিত্তিক বডি শেপ (পেয়ার, আপেল, আওয়ারগ্লাস) দেখতে চাইলে "
              : "This calculator estimates somatotype (endo/meso/ecto). For clothing-fit silhouettes (pear, apple, hourglass) see "}
            <Link to="/body-shape-calculator" className="font-medium text-accent hover:underline">
              {tr("goShape")}
            </Link>.
          </p>
        </Section>

        <Section title={lang === "bn" ? "দাবিত্যাগ" : "Disclaimer"} lang={lang}>
          <p className={cn("text-sm text-muted-foreground", lang === "bn" && "font-bn")}>{tr("disclaimer")}</p>
        </Section>
      </article>
    </CalculatorLayout>
  );
}

function ScoreBar({
  label, value, color, lang, fmt,
}: { label: string; value: number; color: string; lang: "en" | "bn"; fmt: (n: number) => string }) {
  const pct = Math.max(2, Math.min(100, (value / 7) * 100));
  return (
    <div className="mb-3 last:mb-0">
      <div className={cn("mb-1 flex items-baseline justify-between text-xs", lang === "bn" && "font-bn")}>
        <span className="text-muted-foreground">{label}</span>
        <span className="font-semibold tabular-nums text-foreground">{fmt(value)}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ type: "spring", stiffness: 100, damping: 18 }}
          className={cn("h-full rounded-full", color)}
        />
      </div>
    </div>
  );
}

function SomatoTriangle({ result, lang }: { result: BodyTypeResult; lang: "en" | "bn" }) {
  // Triangle with corners at:
  //   Endo (bottom-left), Meso (top), Ecto (bottom-right)
  // Heath-Carter X = ecto - endo (range ~-7..+7), Y = 2*meso - endo - ecto (range ~-7..+14)
  const W = 280, H = 240, pad = 28;
  const cx = W / 2, baseY = H - pad, topY = pad;
  const halfBase = (H - pad * 2) / 1.155; // equilateral height ratio
  const endoX = cx - halfBase, endoY = baseY;
  const ectoX = cx + halfBase, ectoY = baseY;
  const mesoX = cx, mesoY = topY;

  const { x, y } = somatoXY(result);
  // Map x in [-7,7] -> [endoX, ectoX]
  const px = cx + (x / 7) * halfBase;
  // Map y in [-7,14] -> [baseY, topY]
  const yMin = -7, yMax = 14;
  const py = baseY - ((y - yMin) / (yMax - yMin)) * (baseY - topY);

  return (
    <div className="rounded-xl border border-border bg-card p-3">
      <p className={cn("mb-2 px-2 text-xs font-medium text-muted-foreground", lang === "bn" && "font-bn")}>
        {lang === "bn" ? "সোমাটোচার্ট" : "Somatochart"}
      </p>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full">
        <polygon
          points={`${endoX},${endoY} ${ectoX},${ectoY} ${mesoX},${mesoY}`}
          className="fill-accent/5 stroke-border"
          strokeWidth={1.5}
        />
        {/* corner labels */}
        <text x={endoX - 4} y={endoY + 14} className="fill-muted-foreground text-[10px]" textAnchor="end">Endo</text>
        <text x={ectoX + 4} y={ectoY + 14} className="fill-muted-foreground text-[10px]">Ecto</text>
        <text x={mesoX} y={mesoY - 6} className="fill-muted-foreground text-[10px]" textAnchor="middle">Meso</text>
        <motion.circle
          initial={{ r: 0, cx: cx, cy: baseY }}
          animate={{ r: 6, cx: px, cy: py }}
          transition={{ type: "spring", stiffness: 120, damping: 18 }}
          className="fill-accent stroke-background"
          strokeWidth={2}
        />
      </svg>
    </div>
  );
}

function Section({ title, children, lang }: { title: string; children: React.ReactNode; lang: "en" | "bn" }) {
  return (
    <section>
      <h2 className={cn("mb-3 text-lg font-bold tracking-tight", lang === "bn" && "font-bn")}>{title}</h2>
      {children}
    </section>
  );
}
